import { Suspense, lazy, useState, type ComponentType } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Shell } from './components/Shell';
import { DEFAULT_ROUTE, NAV_GROUPS } from './nav';
import { ApiError, api, type Me } from './lib/api';
import { ActiveCalls } from './pages/ActiveCalls';
import { CallFlows } from './pages/CallFlows';
import { CallFeatures } from './pages/CallFeatures';
import { Cdr } from './pages/Cdr';
import { Dashboard } from './pages/Dashboard';
import { Devices } from './pages/Devices';
import { Events } from './pages/Events';
import { ExtensionGroups } from './pages/ExtensionGroups';
import { Extensions } from './pages/Extensions';
import { FeatureCodes } from './pages/FeatureCodes';
import { Login } from './pages/Login';
import { OperationLog } from './pages/OperationLog';
import { Prompts } from './pages/Prompts';
import { Recording } from './pages/Recording';
import { Recordings } from './pages/Recordings';
import { Reports } from './pages/Reports';
import { InboundRoutes, OutboundRoutes, Routing } from './pages/Routing';
import { ApiReference } from './pages/ApiReference';
import { Maintenance } from './pages/Maintenance';
import { Security } from './pages/Security';
import { Storage } from './pages/Storage';
import { SdkCredentials } from './pages/SdkCredentials';
import { Settings } from './pages/Settings';
import { Setup } from './pages/Setup';
import { System } from './pages/System';
import { TimeConditions } from './pages/TimeConditions';
import { Trunks } from './pages/Trunks';
import { Users } from './pages/Users';

/**
 * Loaded on demand, unlike every other page.
 *
 * It pulls in the SDK and with it a whole SIP stack — about half the bundle. Splitting it keeps
 * that weight off the first paint for the majority of sessions that never open a softphone, and
 * it contains the blast radius: a module-level failure in the SIP stack now breaks this page
 * instead of the entire admin, which is exactly how a blank white page happened once already.
 */
const Softphone = lazy(() => import('./pages/Softphone').then((m) => ({ default: m.Softphone })));

const PAGES: Record<string, ComponentType> = {
  dashboard: Dashboard,
  'active-calls': ActiveCalls,
  extensions: Extensions,
  'extension-groups': ExtensionGroups,
  softphone: Softphone,
  trunks: Trunks,
  routing: Routing,
  'inbound-routes': InboundRoutes,
  'outbound-routes': OutboundRoutes,
  flows: CallFlows,
  prompts: Prompts,
  'call-features': CallFeatures,
  'feature-codes': FeatureCodes,
  devices: Devices,
  'time-conditions': TimeConditions,
  history: Cdr,
  recordings: Recordings,
  recording: Recording,
  reports: Reports,
  'sdk-credentials': SdkCredentials,
  'api-reference': ApiReference,
  // Config history, the generated-file viewer and the hostname settings. The setup wizard is
  // still shown automatically when unconfigured; this is how it is reached afterwards.
  system: System,
  events: Events,
  'operation-log': OperationLog,
  maintenance: Maintenance,
  security: Security,
  storage: Storage,
  settings: Settings,
  // Rendered directly rather than through PAGES: it is the one screen that needs to know who
  // is signed in, so that role checks match what the API will actually allow.
  users: () => null,
};

export function App() {
  const queryClient = useQueryClient();
  const [route, setRoute] = useState<string>(DEFAULT_ROUTE);

  const {
    data: me,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<Me>('/api/v1/auth/me'),
    // A 401 is the normal not-signed-in state, not a transient failure worth retrying.
    retry: (count, err) => !(err instanceof ApiError && err.isUnauthenticated) && count < 2,
  });

  const logout = useMutation({
    mutationFn: () => api.post('/api/v1/auth/logout'),
    onSuccess: () => queryClient.clear(),
  });

  if (isLoading) return null;

  if (!me || (error instanceof ApiError && error.isUnauthenticated)) {
    return <Login onAuthenticated={() => void queryClient.invalidateQueries()} />;
  }

  return (
    <Authenticated me={me} route={route} setRoute={setRoute} onSignOut={() => logout.mutate()} />
  );
}

function Authenticated({
  me,
  route,
  setRoute,
  onSignOut,
}: {
  me: Me;
  route: string;
  setRoute: (r: string) => void;
  onSignOut: () => void;
}) {
  const [setupDismissed, setSetupDismissed] = useState(false);

  const { data: setup } = useQuery({
    queryKey: ['setup-status'],
    queryFn: () =>
      api.get<{ hostname: string | null; dns: { matches: boolean } | null }>(
        '/api/v1/setup/status',
      ),
  });

  // The hostname becomes the SIP realm and is baked into every credential, so it has to be
  // settled before anything is created. Showing the wizard first is what makes that ordering
  // happen without relying on the admin reading documentation.
  const needsSetup = setup !== undefined && (!setup.hostname || !setup.dns?.matches);
  if (needsSetup && !setupDismissed) {
    return <Setup onComplete={() => setSetupDismissed(true)} />;
  }

  const Page = PAGES[route] ?? PAGES[DEFAULT_ROUTE]!;

  return (
    <Shell
      groups={NAV_GROUPS}
      route={route}
      setRoute={setRoute}
      email={me.email}
      onSignOut={onSignOut}
    >
      <Suspense fallback={<p className="text-sm text-muted-foreground">Loading…</p>}>
        {route === 'users' ? <Users me={me} /> : <Page />}
      </Suspense>
    </Shell>
  );
}
